/**
 * V2 Admin Outreach — Michael's gated warm-outreach engine.
 *
 * GATED MVP (2026-06-27): contacts CSV import + ONE segment-by-tag + composer
 * with merge fields + branded preview + gated send (one campaign at a time,
 * no scheduling) + unsubscribe + suppression + basic per-campaign stats.
 *
 * 🔴 SAFETY: nothing can send without an explicit click on "Start sending"
 * inside the confirm modal. The /start endpoint requires {confirm:true} in
 * the body — there is no way to fire a campaign through this UI without
 * Michael's deliberate action.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Chrome, C } from './V2AdminOSPage';
import V2EmailComposer from './V2EmailComposer';
import V2VoiceGuideModal from './V2VoiceGuideModal';

const API = process.env.REACT_APP_BACKEND_URL;

const TOKEN_KEY = 'kindra-admin-token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ---------- styles (shared OS dark cockpit) ----------
const navy = '#100838';
const icy  = '#DCF0F5';
const teal = '#34D1B0';
const magenta = '#EB258D';
const surface = '#1A1150';
const line = '#2A2070';
const spectrum = 'linear-gradient(95deg, #34D1B0 0%, #1FB6C1 25%, #4F8FD0 50%, #9156C4 75%, #EB258D 100%)';

const card = {
  background: surface, border: `1px solid ${line}`, borderRadius: 12,
  padding: 20, marginBottom: 16,
};
const btn = (bg, fg='#fff') => ({
  display: 'inline-flex', alignItems: 'center', gap: 8,
  background: bg, color: fg, border: 'none', borderRadius: 999,
  padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  letterSpacing: '.02em', fontFamily: 'Inter, sans-serif',
});
const ghost = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  background: 'transparent', color: icy, borderRadius: 999,
  border: '1.5px solid rgba(220,240,245,0.35)',
  padding: '9px 17px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  letterSpacing: '.02em', fontFamily: 'Inter, sans-serif',
};
const input = {
  width: '100%', padding: '10px 12px', fontSize: 14, border: `1px solid ${line}`,
  borderRadius: 8, fontFamily: 'Inter, sans-serif', background: navy,
  color: icy, outline: 'none',
};

// ---------- subcomponents ----------
const Section = ({ title, right, children, testid }) => (
  <div data-testid={testid} style={card}>
    <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:14 }}>
      <h3 style={{ margin:0, fontFamily:'Poppins,Inter,sans-serif', fontSize:18, fontWeight:700, color:icy }}>{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const Stat = ({ label, value, tint }) => (
  <div style={{ flex: 1, minWidth: 120, padding: '12px 16px', background: 'rgba(220,240,245,0.05)', borderRadius: 8, border: `1px solid ${line}` }}>
    <div style={{ fontSize: 11, letterSpacing:'.10em', textTransform:'uppercase', color:'rgba(220,240,245,0.55)', fontWeight:600 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: tint || icy, marginTop: 4, fontFamily: 'Poppins,Inter,sans-serif' }}>{value}</div>
  </div>
);

// ---------- main page ----------
export default function V2AdminOutreachPage() {
  const nav = useNavigate();
  const [contacts, setContacts] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [campaigns, setCampaigns] = React.useState([]);
  const [activeCampaign, setActiveCampaign] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [preflight, setPreflight] = React.useState(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [composerLead, setComposerLead] = React.useState(null);
  // Desk → Studio handshake: /admin/outreach?compose=1&lead=<id> opens pre-loaded
  React.useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('compose') !== '1') return;
    const lid = sp.get('lead');
    (async () => {
      if (lid) {
        try {
          const r = await fetch(`${API}/api/admin/os/leads/${lid}`, { headers: authHeaders() });
          const d = await r.json();
          if (d.lead) setComposerLead(d.lead);
        } catch { /* compose without lead */ }
      }
      setComposerOpen(true);
      window.history.replaceState({}, '', '/admin/outreach');
    })();
  }, []);
  const [voiceOpen, setVoiceOpen] = React.useState(false);
  const [toast, setToast] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [pacing, setPacing] = React.useState(null);
  const [rep, setRep] = React.useState(null);
  const [blockPerf, setBlockPerf] = React.useState([]);
  // Contact detail modal — full record for one contact (linked lead,
  // quotes, jobs, sends, replies, tombstone status). Loaded lazily when
  // MJ clicks the ℹ button on a contact row.
  const [detailFor, setDetailFor] = React.useState(null);   // email string
  const [detail, setDetail] = React.useState(null);
  const [detailBusy, setDetailBusy] = React.useState(false);
  const openDetail = async (email) => {
    setDetailFor(email); setDetail(null); setDetailBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/contacts/${encodeURIComponent(email)}/detail`,
                              { headers: authHeaders() });
      if (!r.ok) throw new Error(`http ${r.status}`);
      setDetail(await r.json());
    } catch (e) { showToast('Could not load detail: ' + e.message); }
    setDetailBusy(false);
  };
  const closeDetail = () => { setDetailFor(null); setDetail(null); };
  const archiveContact = async (email, archived) => {
    if (!confirm(`${archived ? 'Archive' : 'Restore'} contact ${email}?`)) return;
    try {
      const r = await fetch(`${API}/api/admin/outreach/contacts/archive`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type':'application/json' },
        body: JSON.stringify({ email, archived }),
      });
      if (!r.ok) throw new Error(`http ${r.status}`);
      showToast(archived ? '🗄 Archived' : '↺ Restored');
      closeDetail(); reloadContacts?.();
    } catch (e) { showToast('Failed: ' + e.message); }
  };

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const fetchAll = React.useCallback(async () => {
    try {
      const [cs, st, cmps, pc, rp, bp] = await Promise.all([
        fetch(`${API}/api/admin/outreach/contacts?limit=200`, { headers: authHeaders() }).then(r => r.ok ? r.json() : { contacts: [] }),
        fetch(`${API}/api/admin/outreach/contacts/stats`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/admin/outreach/campaigns`, { headers: authHeaders() }).then(r => r.ok ? r.json() : { campaigns: [] }),
        fetch(`${API}/api/admin/outreach/pacing`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/admin/outreach/reputation`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/admin/outreach/block_performance`, { headers: authHeaders() }).then(r => r.ok ? r.json() : { blocks: [] }),
      ]);
      setBlockPerf(bp?.blocks || []);
      setContacts(cs.contacts || []);
      setStats(st);
      setCampaigns(cmps.campaigns || []);
      setPacing(pc);
      setRep(rp);
    } catch (e) { showToast('Failed to load: ' + e.message); }
  }, []);

  React.useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { const r=encodeURIComponent(window.location.pathname+window.location.search); window.location.assign('/admin/login?returnTo='+r); return; }
    fetchAll();
  }, [fetchAll, nav]);

  // ---- contacts import ----
  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('default_tag', prompt('Default tag for these contacts?', 'outreach-test') || 'outreach-test');
      const r = await fetch(`${API}/api/admin/outreach/contacts/import`, {
        method: 'POST', headers: authHeaders(), body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || JSON.stringify(d));
      showToast(`Imported: inserted ${d.inserted}, updated ${d.updated}, invalid ${d.invalid}`);
      fetchAll();
    } catch (e) { showToast('Import failed: ' + e.message); }
    finally { setBusy(false); e.target.value = ''; }
  };

  // ---- campaign create / select ----
  const createCampaign = async () => {
    const name = prompt('Campaign name?');
    if (!name) return;
    const r = await fetch(`${API}/api/admin/outreach/campaigns`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, subject: '{{first_name}}, quick one', body: 'Hi {{first_name}},\n\n— Michael', throttle_per_hour: 60 }),
    });
    const d = await r.json();
    if (r.ok) { setActiveCampaign(d); fetchAll(); }
    else showToast('Create failed: ' + (d.detail || ''));
  };

  const saveCampaign = async (patch) => {
    if (!activeCampaign) return;
    const r = await fetch(`${API}/api/admin/outreach/campaigns/${activeCampaign.campaign_id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    const d = await r.json();
    if (r.ok) { setActiveCampaign(d); fetchAll(); showToast('Saved'); }
    else showToast('Save failed: ' + (d.detail || ''));
  };

  const runPreview = async () => {
    if (!activeCampaign) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/campaigns/${activeCampaign.campaign_id}/preview`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await r.json();
      if (r.ok) setPreview(d);
      else showToast('Preview failed');
    } finally { setBusy(false); }
  };

  const runPreflight = async () => {
    if (!activeCampaign) return;
    const r = await fetch(`${API}/api/admin/outreach/campaigns/${activeCampaign.campaign_id}/preflight`, {
      method: 'POST', headers: authHeaders(),
    });
    const d = await r.json();
    if (r.ok) { setPreflight(d); setConfirmOpen(true); }
    else showToast('Preflight failed');
  };

  const startCampaign = async () => {
    if (!activeCampaign) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/campaigns/${activeCampaign.campaign_id}/start`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true }),
      });
      const d = await r.json();
      if (r.ok) { setConfirmOpen(false); showToast('🚀 Campaign started — sending throttled to ' + activeCampaign.throttle_per_hour + '/hr'); fetchAll(); }
      else showToast('Start failed: ' + (d.detail || ''));
    } finally { setBusy(false); }
  };

  const pauseCampaign = async () => {
    if (!activeCampaign) return;
    const r = await fetch(`${API}/api/admin/outreach/campaigns/${activeCampaign.campaign_id}/pause`, {
      method: 'POST', headers: authHeaders(),
    });
    if (r.ok) { showToast('Paused'); fetchAll(); }
  };

  const testSendOne = async () => {
    if (!activeCampaign) return;
    const email = prompt('Test-send to which existing contact email?');
    if (!email) return;
    const r = await fetch(`${API}/api/admin/outreach/campaigns/${activeCampaign.campaign_id}/test_send`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const d = await r.json();
    if (r.ok) showToast(d.ok ? '✓ Test sent to ' + email : 'Skipped: ' + (d.reason || ''));
    else showToast('Test-send failed: ' + (d.detail || ''));
  };

  // ---- P0-B: single-contact add + quick-send (with preview) ----
  const [addForm, setAddForm] = React.useState({ email:'', first_name:'', business_name:'', last_name:'', phone:'', notes:'' });
  const [quickTarget, setQuickTarget] = React.useState(null); // {contact, campaign_id, preview}
  const [selectedEmails, setSelectedEmails] = React.useState(() => new Set());

  // ---- Campaign audience manager (2026-07-02) ----
  // audience = full recipient list for the active campaign, always kept
  // in sync when the campaign switches or the audience mutates. Adding
  // never sends — it only updates campaign_ids on the contacts.
  const [audience, setAudience] = React.useState(null);
  const [audienceBusy, setAudienceBusy] = React.useState(false);
  const [audienceEmailsSelected, setAudienceEmailsSelected] = React.useState(() => new Set());

  const fetchAudience = React.useCallback(async (cid) => {
    if (!cid) { setAudience(null); return; }
    try {
      const r = await fetch(`${API}/api/admin/outreach/campaigns/${cid}/audience`, { headers: authHeaders() });
      if (r.ok) setAudience(await r.json());
    } catch (e) { /* silent — UI will show empty */ }
  }, []);

  // Refetch audience whenever active campaign changes so the count/list
  // in the audience panel is never stale.
  React.useEffect(() => {
    fetchAudience(activeCampaign?.campaign_id);
    setAudienceEmailsSelected(new Set());
  }, [activeCampaign?.campaign_id, fetchAudience]);

  const addEmailsToActiveAudience = async (emails) => {
    if (!activeCampaign) { showToast('Pick a campaign first.'); return; }
    const cleaned = [...new Set((emails || []).map((e) => (e || '').toLowerCase().trim()).filter(Boolean))];
    if (cleaned.length === 0) { showToast('No contacts selected.'); return; }
    setAudienceBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/campaigns/${activeCampaign.campaign_id}/audience/add`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: cleaned }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'add failed');
      showToast(`Added ${d.added || 0} to “${activeCampaign.name}” (no email sent)`);
      setSelectedEmails(new Set());
      await fetchAudience(activeCampaign.campaign_id);
    } catch (e) { showToast('Add to campaign failed: ' + e.message); }
    finally { setAudienceBusy(false); }
  };

  const removeEmailsFromAudience = async (emails) => {
    if (!activeCampaign) return;
    if (!(emails || []).length) return;
    setAudienceBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/campaigns/${activeCampaign.campaign_id}/audience/remove`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'remove failed');
      showToast(`Removed ${d.removed || 0} from audience`);
      setAudienceEmailsSelected((prev) => {
        const s = new Set(prev); emails.forEach((e) => s.delete(e)); return s;
      });
      await fetchAudience(activeCampaign.campaign_id);
    } catch (e) { showToast('Remove failed: ' + e.message); }
    finally { setAudienceBusy(false); }
  };

  const toggleAudienceSelect = (email) => {
    setAudienceEmailsSelected((prev) => {
      const s = new Set(prev); if (s.has(email)) s.delete(email); else s.add(email); return s;
    });
  };

  const addContact = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/contacts`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'add failed');
      showToast(`✓ Added ${d.contact.first_name} — ${d.contact.email}`);
      setAddForm({ email:'', first_name:'', business_name:'', last_name:'', phone:'', notes:'' });
      fetchAll();
    } catch (e) { showToast('Add failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const startQuickSend = async (contact) => {
    if (!activeCampaign) { showToast('Pick a campaign first (click it in the Campaigns list).'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/campaigns/${activeCampaign.campaign_id}/preview`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: contact.email }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'preview failed');
      setQuickTarget({ contact, campaign_id: activeCampaign.campaign_id, preview: d });
    } catch (e) { showToast('Preview failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const confirmQuickSend = async () => {
    if (!quickTarget) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/quick_send`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: quickTarget.campaign_id,
          contact_email: quickTarget.contact.email,
          confirm: true,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'send failed');
      const mode = d.test_mode ? ' (test mode — sent to michael@kindraai.dev)' : '';
      showToast(`✓ Sent to ${quickTarget.contact.first_name}${mode}`);
      setQuickTarget(null);
      fetchAll();
    } catch (e) { showToast('Quick send failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const deleteContacts = async (emails) => {
    if (!emails.length) return;
    if (!confirm(`Delete ${emails.length} contact${emails.length===1?'':'s'}?\n\nUnsubscribes stay on file — a re-added contact stays unsubscribed. This protects deliverability.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/outreach/contacts/delete`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails, confirm: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'delete failed');
      showToast(`✓ Deleted ${d.deleted}${d.suppression_preserved ? ' (suppression preserved)' : ''}`);
      setSelectedEmails(new Set());
      fetchAll();
    } catch (e) { showToast('Delete failed: ' + e.message); }
    finally { setBusy(false); }
  };

  const toggleSelect = (email) => {
    setSelectedEmails(prev => {
      const s = new Set(prev);
      if (s.has(email)) s.delete(email); else s.add(email);
      return s;
    });
  };

  // ---- render ----
  return (
    <Chrome activeTab="outreach">
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
        <p style={{ fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: C.faint, fontFamily: 'Inter' }}>Kindra OS · Hunting Grounds</p>
        <h1 style={{ margin: '6px 0 0', fontSize: 'clamp(24px,3vw,30px)', fontWeight: 600, fontFamily: 'Poppins,Inter,sans-serif', color: icy }}>Hunting Grounds</h1>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: C.muted, fontFamily: 'Inter', maxWidth: 640 }}>
          Find and email your contacts. Every send needs your confirmation click — unsubscribes are honoured automatically.
        </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button data-testid="open-voice-guide-btn" onClick={() => setVoiceOpen(true)} style={ghost}>Voice guide</button>
          <button data-testid="open-composer-btn" onClick={() => setComposerOpen(true)} style={btn(spectrum)}>✨ New branded email</button>
        </div>
      </div>
      {voiceOpen && <V2VoiceGuideModal onClose={() => setVoiceOpen(false)} showToast={showToast} />}

      {/* WARM-UP PACING — today's sends vs cap (protects domain reputation) */}
      {pacing && (
        <div data-testid="pacing-tile" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
             background: 'rgba(26,17,80,0.55)', border: `1px solid ${pacing.remaining === 0 ? 'rgba(240,195,109,0.5)' : C.hair}`,
             borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
          <div style={{ minWidth: 170 }}>
            <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#F0C36D' }}>
              Domain warm-up · week {pacing.week}
            </p>
            <p data-testid="pacing-count" style={{ margin: '3px 0 0', fontFamily: 'Poppins,Inter,sans-serif', fontSize: 18, fontWeight: 700, color: icy }}>
              {pacing.sent_today} <span style={{ color: C.faint, fontSize: 13, fontWeight: 500 }}>/ {pacing.cap} sent today</span>
            </p>
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ height: 8, borderRadius: 999, background: 'rgba(220,240,245,0.08)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(100, (pacing.sent_today / Math.max(1, pacing.cap)) * 100)}%`,
                            background: pacing.remaining === 0 ? '#F0C36D' : spectrum, borderRadius: 999, transition: 'width 0.4s ease' }} />
            </div>
            <p style={{ margin: '5px 0 0', fontFamily: 'Inter', fontSize: 11, color: C.muted }}>
              {pacing.remaining === 0
                ? 'Today\u2019s warm-up cap reached — sends will ask before exceeding it. Campaigns auto-resume tomorrow.'
                : `${pacing.remaining} left today · ramp ${(pacing.ramp || []).join(' → ')} → ${pacing.final_cap}/day · min ${pacing.min_spacing_seconds}s between bulk sends`}
            </p>
          </div>

          {/* REPUTATION — only what Resend + our pixel/replies can measure */}
          {rep && (
            <div data-testid="reputation-panel" style={{ flexBasis: '100%', borderTop: `1px solid ${C.hair}`, paddingTop: 12, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {[
                { label: 'Delivered', v: rep.last7.delivered, rate: rep.last7.delivery_rate, col: '#34D1B0' },
                { label: 'Opened', v: rep.last7.opened, rate: rep.last7.open_rate, col: '#5FE0BE' },
                { label: 'Replied', v: rep.last7.replied, rate: rep.last7.reply_rate, col: '#1FB6C1' },
                { label: 'Bounced', v: rep.last7.bounced, rate: rep.last7.bounce_rate, col: rep.bounce_warning ? '#FF7DB3' : C.muted },
              ].map((m) => (
                <div key={m.label} data-testid={`rep-${m.label.toLowerCase()}`} style={{ minWidth: 86 }}>
                  <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.faint }}>{m.label} · 7d</p>
                  <p style={{ margin: '2px 0 0', fontFamily: 'Poppins,Inter,sans-serif', fontSize: 16, fontWeight: 700, color: m.col }}>
                    {m.rate}% <span style={{ fontSize: 11, color: C.faint, fontWeight: 500 }}>({m.v}/{rep.last7.sent})</span>
                  </p>
                </div>
              ))}
              {/* 14-day sent vs opened sparkline */}
              <div data-testid="rep-sparkline" style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 40, marginLeft: 'auto' }}
                   title="Last 14 days — bar = sent, bright segment = opened">
                {(rep.days || []).map((d) => {
                  const max = Math.max(1, ...(rep.days || []).map((x) => x.sent));
                  const h = Math.max(d.sent > 0 ? 4 : 2, Math.round((d.sent / max) * 40));
                  const oh = d.sent > 0 ? Math.round((d.opened / d.sent) * h) : 0;
                  return (
                    <div key={d.date} title={`${d.date}: ${d.sent} sent · ${d.opened} opened · ${d.bounced} bounced`}
                         style={{ width: 8, height: h, borderRadius: 2, background: 'rgba(220,240,245,0.14)', position: 'relative', overflow: 'hidden' }}>
                      {oh > 0 && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: oh, background: '#5FE0BE' }} />}
                      {d.bounced > 0 && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#FF7DB3' }} />}
                    </div>
                  );
                })}
              </div>
              <p style={{ flexBasis: '100%', margin: 0, fontFamily: 'Inter', fontSize: 10.5, color: C.faint }}>
                Honest numbers only: "delivered" = their mail server accepted it — inbox vs junk isn't measurable from here. Opens/replies via our pixel + reply tracking.
              </p>
              {rep.bounce_warning && (
                <p data-testid="bounce-warning" style={{ flexBasis: '100%', margin: 0, fontFamily: 'Inter', fontSize: 12, fontWeight: 700, color: '#FF7DB3',
                     background: 'rgba(255,125,179,0.08)', border: '1px solid rgba(255,125,179,0.35)', borderRadius: 8, padding: '8px 12px' }}>
                  ⚠ Bounce rate {rep.last7.bounce_rate}% over the last 7 days is above the healthy {rep.bounce_warn_threshold_pct}% line — ease off the volume and clean the list (verify addresses) before ramping further. Bounces are the fastest way to tank a new domain's reputation.
                </p>
              )}
            </div>
          )}

          {/* LAYOUT SIGNALS — which blocks earn replies (replies only, opens excluded) */}
          {blockPerf.length > 0 && (
            <div data-testid="layout-signals-panel" style={{ flexBasis: '100%', borderTop: `1px solid ${C.hair}`, paddingTop: 12 }}>
              <p style={{ margin: '0 0 8px', fontFamily: 'Inter', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.faint }}>
                Layout signals · which blocks get replies <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>— replies only, the co-writer leans toward winners (never forced)</span>
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {blockPerf.slice(0, 8).map((b) => (
                  <div key={b.block_id} data-testid={`layout-signal-${b.block_id}`}
                       style={{ borderRadius: 10, padding: '6px 12px', background: 'rgba(220,240,245,0.05)', border: `1px solid ${C.hair}` }}>
                    <span style={{ fontFamily: 'Inter', fontSize: 11.5, fontWeight: 700, color: '#DCEFF5' }}>{b.name}</span>
                    <span style={{ fontFamily: 'Inter', fontSize: 11, color: b.replies > 0 ? '#5FE0BE' : C.faint, marginLeft: 8 }}>
                      {b.replies > 0 ? `${Math.round(b.reply_rate * 100)}% replied` : 'no replies yet'} · {b.replies}/{b.sent}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {composerOpen && (
        <V2EmailComposer onClose={() => { setComposerOpen(false); setComposerLead(null); }} campaigns={campaigns} showToast={showToast} leadContext={composerLead} />
      )}
        {/* CONTACTS */}
        <Section
          title="Contacts"
          testid="outreach-contacts-section"
          right={
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              {selectedEmails.size > 0 && (
                <>
                  <select
                    data-testid="outreach-add-to-campaign-picker"
                    disabled={audienceBusy || campaigns.length === 0}
                    onChange={(e) => {
                      const cid = e.target.value;
                      if (!cid) return;
                      const c = campaigns.find(x => x.campaign_id === cid);
                      if (c) { setActiveCampaign(c); addEmailsToActiveAudience([...selectedEmails]); }
                      e.target.value = '';
                    }}
                    style={{
                      padding:'8px 12px', borderRadius:999, border:`1px solid ${line}`,
                      background:surface, color:icy, fontSize:12, fontWeight:600, cursor:'pointer',
                    }}>
                    <option value="">+ Add {selectedEmails.size} to campaign…</option>
                    {campaigns.map(c => (
                      <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>
                    ))}
                  </select>
                  <button
                    data-testid="outreach-delete-selected"
                    onClick={() => deleteContacts([...selectedEmails])}
                    style={btn('#EB258D', '#fff')}>
                    Delete {selectedEmails.size} selected
                  </button>
                </>
              )}
              <label data-testid="outreach-import-btn" style={{ ...btn(spectrum), cursor: 'pointer' }}>
                {busy ? '…' : '+ Import CSV'}
                <input type="file" accept=".csv,text/csv" onChange={onImport} style={{ display:'none' }} />
              </label>
            </div>
          }
        >
          {stats && (
            <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
              <Stat label="Active" value={stats.total.active} tint={teal} />
              <Stat label="Unsubscribed" value={stats.total.unsubscribed} tint={magenta} />
              <Stat label="Bounced" value={stats.total.bounced || 0} />
              <Stat label="Invalid" value={stats.total.invalid || 0} />
              <Stat
                label="Resend domain"
                value={stats.resend_verified ? '✓ Verified' : '⚠ NOT VERIFIED'}
                tint={stats.resend_verified ? teal : magenta}
              />
            </div>
          )}
          {/* Add-contact form — one row, minimal, meet-someone-then-add flow */}
          <form onSubmit={addContact} data-testid="outreach-add-contact-form"
                style={{ background:'rgba(220,240,245,0.06)', border:'1px solid #2A2070', borderRadius:10,
                          padding:12, marginBottom:14, display:'grid',
                          gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:8, alignItems:'end' }}>
            <div style={{ gridColumn: '1 / -1', fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'rgba(220,240,245,0.55)', fontWeight:700 }}>
              Add a single contact
            </div>
            <input required data-testid="add-first-name" placeholder="First name *" value={addForm.first_name}
                    onChange={e=>setAddForm({...addForm, first_name:e.target.value})}
                    style={{ padding:'8px 10px', borderRadius:6, border:'1px solid #2A2070', fontSize:13 }} />
            <input required data-testid="add-business" placeholder="Business *" value={addForm.business_name}
                    onChange={e=>setAddForm({...addForm, business_name:e.target.value})}
                    style={{ padding:'8px 10px', borderRadius:6, border:'1px solid #2A2070', fontSize:13 }} />
            <input required type="email" data-testid="add-email" placeholder="Email *" value={addForm.email}
                    onChange={e=>setAddForm({...addForm, email:e.target.value})}
                    style={{ padding:'8px 10px', borderRadius:6, border:'1px solid #2A2070', fontSize:13 }} />
            <input data-testid="add-last-name" placeholder="Last name" value={addForm.last_name}
                    onChange={e=>setAddForm({...addForm, last_name:e.target.value})}
                    style={{ padding:'8px 10px', borderRadius:6, border:'1px solid #2A2070', fontSize:13 }} />
            <input data-testid="add-phone" placeholder="Phone" value={addForm.phone}
                    onChange={e=>setAddForm({...addForm, phone:e.target.value})}
                    style={{ padding:'8px 10px', borderRadius:6, border:'1px solid #2A2070', fontSize:13 }} />
            <input data-testid="add-notes" placeholder="Notes" value={addForm.notes}
                    onChange={e=>setAddForm({...addForm, notes:e.target.value})}
                    style={{ padding:'8px 10px', borderRadius:6, border:'1px solid #2A2070', fontSize:13 }} />
            <button type="submit" disabled={busy} data-testid="add-contact-submit" style={btn(teal, navy)}>
              {busy ? '…' : 'Add contact'}
            </button>
          </form>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background:'rgba(220,240,245,0.06)', textAlign:'left' }}>
                  <th style={{ padding: 8, width: 28 }}></th>
                  <th style={{ padding: 8 }}>Email</th>
                  <th style={{ padding: 8 }}>Name</th>
                  <th style={{ padding: 8 }}>Business</th>
                  <th style={{ padding: 8 }}>Phone</th>
                  <th style={{ padding: 8 }}>Tag</th>
                  <th style={{ padding: 8 }}>Status</th>
                  <th style={{ padding: 8, textAlign:'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.slice(0, 60).map(c => (
                  <tr key={c.email} style={{ borderTop: '1px solid #2A2070' }} data-testid={`contact-row-${c.email}`}>
                    <td style={{ padding: 8 }}>
                      <input type="checkbox"
                              data-testid={`select-${c.email}`}
                              checked={selectedEmails.has(c.email)}
                              onChange={() => toggleSelect(c.email)} />
                    </td>
                    <td style={{ padding: 8, fontFamily: 'monospace', color: icy }}>{c.email}</td>
                    <td style={{ padding: 8, color: icy, fontWeight: 600 }}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td style={{ padding: 8, color: icy }}>{c.business_name || '—'}</td>
                    <td style={{ padding: 8, color: '#B5D5DD', fontSize:12 }}>{c.phone || '—'}</td>
                    <td style={{ padding: 8 }}>
                      <span style={{ background:'rgba(220,240,245,0.06)', padding:'2px 8px', borderRadius:999, border:'1px solid #2A2070', fontSize:11 }}>{c.tag}</span>
                    </td>
                    <td style={{ padding: 8 }}>
                      <span style={{
                        background: c.status==='active' ? 'rgba(52,209,176,0.12)' : 'rgba(235,37,141,0.10)',
                        color: c.status==='active' ? '#5FE0BE' : '#FF7DB3',
                        padding:'2px 10px', borderRadius:999, fontSize:11, fontWeight:600,
                      }}>{c.status}</span>
                    </td>
                    <td style={{ padding: 8, textAlign:'right' }}>
                      {activeCampaign && (
                        <button data-testid={`add-to-audience-${c.email}`}
                                disabled={audienceBusy || !activeCampaign}
                                onClick={() => addEmailsToActiveAudience([c.email])}
                                title={`Add ${c.first_name} to “${activeCampaign.name}” audience (no email sent)`}
                                style={{ ...ghost, padding:'4px 10px', fontSize:11, marginRight:4 }}>
                          + To campaign
                        </button>
                      )}
                      <button data-testid={`quicksend-${c.email}`}
                              disabled={c.status !== 'active' || !activeCampaign || busy}
                              onClick={() => startQuickSend(c)}
                              title={activeCampaign ? `Send an intro email to ${c.first_name} using the "${activeCampaign.name}" campaign template — a preview appears first, nothing sends without your confirmation.` : 'Pick a campaign first (click one in the Campaigns list below)'}
                              style={{ ...btn(teal, navy), padding:'4px 10px', fontSize:11 }}>
                        ✉ Send intro email
                      </button>
                      <button data-testid={`contact-detail-${c.email}`}
                              onClick={() => openDetail(c.email)}
                              title="Open full contact detail (linked lead, quotes, jobs, sends)"
                              style={{ ...ghost, padding:'4px 8px', fontSize:11, marginRight:4 }}>
                        ℹ Detail
                      </button>
                      <button data-testid={`delete-${c.email}`}
                              disabled={busy}
                              onClick={() => deleteContacts([c.email])}
                              style={{ ...btn('transparent', '#FF7DB3'), padding:'4px 8px', fontSize:11, marginLeft: 4, border:'1px solid #EB258D33' }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {contacts.length === 0 && (
              <div style={{ padding: 24, textAlign:'center', color:'rgba(220,240,245,0.55)', fontSize:13 }}>
                No contacts yet — add one above, or import a CSV with columns: <code>first_name, last_name, email, business_name, tag</code>
              </div>
            )}
          </div>
        </Section>

        {/* CAMPAIGNS */}
        <Section
          title="Campaigns"
          testid="outreach-campaigns-section"
          right={<button data-testid="new-campaign-btn" onClick={createCampaign} style={btn(spectrum)}>+ New campaign</button>}
        >
          <div style={{ display:'grid', gap:10 }}>
            {campaigns.map(c => (
              <div
                key={c.campaign_id}
                onClick={() => setActiveCampaign(c)}
                data-testid={`campaign-row-${c.campaign_id}`}
                style={{
                  padding: 14, borderRadius: 10, cursor:'pointer',
                  border: '1px solid ' + (activeCampaign?.campaign_id === c.campaign_id ? teal : line),
                  background: activeCampaign?.campaign_id === c.campaign_id ? 'rgba(52,209,176,0.12)' : surface,
                }}
              >
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <div>
                    <strong>{c.name}</strong>
                    <div style={{ fontSize: 12, color: 'rgba(220,240,245,0.55)', marginTop:2 }}>tag: {c.segment_tag || '—'} · throttle: {c.throttle_per_hour}/hr</div>
                  </div>
                  <div style={{ display:'flex', gap:14, alignItems:'center', fontSize:12 }}>
                    <span><b>{c.sent_count||0}</b> sent</span>
                    <span><b>{c.opened_count||0}</b> opened</span>
                    <span><b>{c.clicked_count||0}</b> clicked</span>
                    <span><b>{c.unsubscribed_count||0}</b> unsub</span>
                    <span style={{
                      background: c.status==='sending' ? '#34D1B0' : c.status==='completed' ? '#9156C4' : c.status==='paused' ? '#EB258D' : 'rgba(220,240,245,0.55)',
                      color:'#fff', padding:'2px 10px', borderRadius:999, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em',
                    }}>{c.status}</span>
                  </div>
                </div>
              </div>
            ))}
            {campaigns.length === 0 && <div style={{ color:'rgba(220,240,245,0.55)', textAlign:'center', padding:24 }}>No campaigns yet.</div>}
          </div>
        </Section>

        {/* COMPOSER */}
        {activeCampaign && (
          <Section
            title={`Compose: ${activeCampaign.name}`}
            testid="outreach-composer-section"
            right={
              <div style={{ display:'flex', gap:8 }}>
                <button data-testid="composer-preview" onClick={runPreview} style={ghost} disabled={busy}>Preview</button>
                <button data-testid="composer-test-send" onClick={testSendOne} style={ghost} disabled={busy}>Test send to one</button>
                {activeCampaign.status === 'sending' ? (
                  <button data-testid="composer-pause" onClick={pauseCampaign} style={btn(magenta)}>⏸ Pause</button>
                ) : (
                  <button data-testid="composer-start" onClick={runPreflight} style={btn(spectrum)} disabled={!activeCampaign.subject || !activeCampaign.body || activeCampaign.status==='completed'}>
                    🚀 Review &amp; start
                  </button>
                )}
              </div>
            }
          >
            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:12 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#B5D5DD' }}>SUBJECT (merge fields supported: <code style={{ background:'rgba(220,240,245,0.06)', padding:'2px 6px', borderRadius:4 }}>{'{{first_name}}'}</code>, <code style={{ background:'rgba(220,240,245,0.06)', padding:'2px 6px', borderRadius:4 }}>{'{{business_name}}'}</code>)
                <input data-testid="composer-subject" defaultValue={activeCampaign.subject} onBlur={(e)=>saveCampaign({subject: e.target.value})} style={{ ...input, marginTop:6 }} />
              </label>
              <label style={{ fontSize:12, fontWeight:600, color:'#B5D5DD' }}>BODY (plain text, blank line = new paragraph)
                <textarea data-testid="composer-body" defaultValue={activeCampaign.body} onBlur={(e)=>saveCampaign({body: e.target.value})} style={{ ...input, marginTop:6, minHeight:200, fontFamily:'inherit' }} />
              </label>
              <div style={{ display:'flex', gap:12 }}>
                <label style={{ fontSize:12, fontWeight:600, color:'#B5D5DD', flex:1 }}>SEGMENT TAG (only contacts with this tag will be emailed)
                  <input data-testid="composer-tag" defaultValue={activeCampaign.segment_tag || ''} onBlur={(e)=>saveCampaign({segment_tag: e.target.value})} style={{ ...input, marginTop:6 }} />
                </label>
                <label style={{ fontSize:12, fontWeight:600, color:'#B5D5DD', width:160 }}>THROTTLE /HR
                  <input data-testid="composer-throttle" type="number" defaultValue={activeCampaign.throttle_per_hour} onBlur={(e)=>saveCampaign({throttle_per_hour: Number(e.target.value)})} style={{ ...input, marginTop:6 }} />
                </label>
              </div>
            </div>

            {preview && (
              <div data-testid="composer-preview-pane" style={{ marginTop:18 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#B5D5DD', marginBottom:6 }}>
                  PREVIEW — rendered for: <code>{preview.sample_contact.email}</code> (subject: <strong>{preview.subject}</strong>)
                </div>
                <iframe
                  data-testid="composer-preview-iframe"
                  title="email-preview"
                  srcDoc={preview.html}
                  style={{ width:'100%', height:680, border:'1px solid #2A2070', borderRadius:8, background:'#fff' }}
                />
              </div>
            )}
          </Section>
        )}

        {/* AUDIENCE PANEL — recipient list + live count + pre-send summary.
            Adding never sends; sending remains gated behind the confirm modal. */}
        {activeCampaign && (
          <Section title={`Audience: ${activeCampaign.name}`} testid="outreach-audience-section"
            right={
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {audienceEmailsSelected.size > 0 && (
                  <button data-testid="audience-remove-selected"
                          onClick={() => removeEmailsFromAudience([...audienceEmailsSelected])}
                          disabled={audienceBusy}
                          style={btn('#EB258D', '#fff')}>
                    Remove {audienceEmailsSelected.size} from audience
                  </button>
                )}
                <button data-testid="audience-refresh"
                        onClick={() => fetchAudience(activeCampaign.campaign_id)}
                        style={ghost} disabled={audienceBusy}>↻ Refresh</button>
              </div>
            }
          >
            {/* Pre-send summary — Michael's go/no-go glance */}
            <div data-testid="audience-presend-summary" style={{
              background: 'rgba(220,240,245,0.04)',
              border: `1px solid ${line}`, borderRadius: 12, padding: 16, marginBottom: 16,
              display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap: 14,
            }}>
              <div>
                <div style={{ fontSize:10, letterSpacing:'.18em', textTransform:'uppercase', color:'rgba(220,240,245,0.55)', fontWeight:700 }}>Subject</div>
                <div style={{ marginTop:4, fontWeight:600, color:icy, fontSize:14 }}>{activeCampaign.subject || <em style={{color:'#EB258D'}}>(empty — set in composer above)</em>}</div>
              </div>
              <div>
                <div style={{ fontSize:10, letterSpacing:'.18em', textTransform:'uppercase', color:'rgba(220,240,245,0.55)', fontWeight:700 }}>Sender</div>
                <div style={{ marginTop:4, fontWeight:600, color:icy, fontSize:13 }}>{audience?.sender || 'Michael Jackson · Kindra AI <michael@kindraai.dev>'}</div>
              </div>
              <div>
                <div style={{ fontSize:10, letterSpacing:'.18em', textTransform:'uppercase', color:'rgba(220,240,245,0.55)', fontWeight:700 }}>Will send to</div>
                <div data-testid="audience-count" style={{ marginTop:4, fontSize:28, fontWeight:800, color:audience?.count_active ? teal : magenta, lineHeight:1 }}>
                  {audience?.count_active ?? 0}
                </div>
                <div style={{ fontSize:11, color:'rgba(220,240,245,0.55)', marginTop:2 }}>
                  {audience?.count_suppressed > 0 && `+ ${audience.count_suppressed} suppressed (won't receive)`}
                </div>
              </div>
              <div>
                <div style={{ fontSize:10, letterSpacing:'.18em', textTransform:'uppercase', color:'rgba(220,240,245,0.55)', fontWeight:700 }}>Last edited</div>
                <div style={{ marginTop:4, fontSize:13, color:icy }}>{(audience?.last_edited || activeCampaign.updated_at || activeCampaign.created_at || '').slice(0,16).replace('T',' ')}</div>
              </div>
              <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'flex-end' }}>
                {activeCampaign.status === 'sending' ? (
                  <button data-testid="audience-pause" onClick={pauseCampaign} style={btn(magenta)}>⏸ Pause</button>
                ) : (
                  <button data-testid="audience-start" onClick={runPreflight}
                          disabled={!activeCampaign.subject || !activeCampaign.body || activeCampaign.status==='completed' || (audience?.count_active ?? 0) === 0}
                          title={(audience?.count_active ?? 0) === 0 ? 'Audience is empty — add contacts first' : 'Review & start'}
                          style={btn(magenta)}>
                    🚀 Review &amp; start
                  </button>
                )}
              </div>
            </div>

            {/* Recipient list */}
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'rgba(220,240,245,0.06)', textAlign:'left' }}>
                    <th style={{ padding:8, width:28 }}></th>
                    <th style={{ padding:8 }}>Name</th>
                    <th style={{ padding:8 }}>Business</th>
                    <th style={{ padding:8 }}>Email</th>
                    <th style={{ padding:8 }}>Source</th>
                    <th style={{ padding:8 }}>Status</th>
                    <th style={{ padding:8, textAlign:'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="audience-list">
                  {(audience?.contacts || []).map((c) => {
                    const suppressed = c.status !== 'active';
                    return (
                      <tr key={c.email} data-testid={`audience-row-${c.email}`}
                          style={{
                            borderTop: '1px solid #2A2070',
                            opacity: suppressed ? 0.55 : 1,
                            background: suppressed ? 'rgba(220,240,245,0.06)' : 'transparent',
                          }}>
                        <td style={{ padding:8 }}>
                          <input type="checkbox" data-testid={`audience-select-${c.email}`}
                                  checked={audienceEmailsSelected.has(c.email)}
                                  onChange={() => toggleAudienceSelect(c.email)} />
                        </td>
                        <td style={{ padding:8, color:icy, fontWeight:600 }}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                        <td style={{ padding:8, color:icy }}>{c.business_name || '—'}</td>
                        <td style={{ padding:8, fontFamily:'monospace', color:icy, fontSize:12 }}>{c.email}</td>
                        <td style={{ padding:8 }}>
                          <span style={{
                            background: c.source === 'explicit' ? 'rgba(31,182,193,0.14)' : 'rgba(122,113,136,0.12)',
                            color: c.source === 'explicit' ? '#7FD8E2' : '#B5D5DD',
                            padding:'2px 8px', borderRadius:999, fontSize:11, fontWeight:600,
                          }}>{c.source}</span>
                        </td>
                        <td style={{ padding:8 }}>
                          <span style={{
                            background: suppressed ? 'rgba(235,37,141,0.10)' : 'rgba(52,209,176,0.12)',
                            color: suppressed ? '#FF7DB3' : '#5FE0BE',
                            padding:'2px 10px', borderRadius:999, fontSize:11, fontWeight:600,
                          }} title={c.reason_excluded || ''}>
                            {c.reason_excluded ? `⛔ ${c.reason_excluded}` : c.status}
                          </span>
                        </td>
                        <td style={{ padding:8, textAlign:'right' }}>
                          <button data-testid={`audience-remove-${c.email}`}
                                  disabled={audienceBusy}
                                  onClick={() => removeEmailsFromAudience([c.email])}
                                  style={{ ...btn('transparent', '#FF7DB3'), padding:'4px 8px', fontSize:11, border:'1px solid #EB258D33' }}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(!audience || audience.count_total === 0) && (
                <div data-testid="audience-empty" style={{ padding:24, textAlign:'center', color:'rgba(220,240,245,0.55)', fontSize:13 }}>
                  Audience is empty. Tick contacts above and use “+ Add {'{n}'} to campaign…”, or use each contact&apos;s <em>+ To campaign</em> button.
                </div>
              )}
            </div>
          </Section>
        )}

      {/* CONTACT DETAIL MODAL — full record for one HG contact.
          Data comes from GET /api/admin/outreach/contacts/{email}/detail
          (contact + linked lead + quotes + jobs + sends + replies +
          tombstone). Kindra-styled, thin scrollbars, archive/delete
          actions inline. */}
      {detailFor && (
        <div data-testid="contact-detail-modal" style={{
          position:'fixed', inset:0, background:'rgba(16,8,56,0.75)', backdropFilter:'blur(4px)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:100, padding:24,
        }} onClick={closeDetail}>
          <div className="studio-scroll" onClick={(e) => e.stopPropagation()} style={{
            background:surface, border:`1px solid ${line}`, borderRadius:16, maxWidth:720,
            width:'100%', maxHeight:'85vh', overflowY:'auto', padding:'28px 32px',
          }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:16, marginBottom:6 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, letterSpacing:'.22em', textTransform:'uppercase', color:teal, fontWeight:700 }}>Contact detail</div>
                <h2 style={{ margin:'6px 0 4px', fontFamily:'Poppins,Inter,sans-serif', fontSize:22, color:icy }}>
                  {detail?.contact ? [detail.contact.first_name, detail.contact.last_name].filter(Boolean).join(' ') || detailFor : detailFor}
                </h2>
                <div style={{ fontFamily:'monospace', color:'#B5D5DD', fontSize:12.5 }}>{detailFor}</div>
              </div>
              <button data-testid="contact-detail-close" onClick={closeDetail}
                style={{ ...ghost, padding:'6px 12px', fontSize:16 }}>✕</button>
            </div>

            {detailBusy && <p style={{ color:'#B5D5DD', margin:'20px 0' }}>Loading…</p>}
            {detail && (<>
              {/* Contact fields */}
              <div style={{ marginTop:18, padding:16, background:'rgba(255,255,255,0.03)', borderRadius:12, border:`1px solid ${line}` }}>
                <div style={{ fontSize:11, color:'#B5D5DD', letterSpacing:'.14em', textTransform:'uppercase', fontWeight:700, marginBottom:8 }}>Contact fields</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 20px', fontSize:13, color:icy }}>
                  <div><b style={{ opacity:0.6 }}>Business:</b> {detail.contact.business_name || '—'}</div>
                  <div><b style={{ opacity:0.6 }}>Phone:</b> {detail.contact.phone || '—'}</div>
                  <div><b style={{ opacity:0.6 }}>Tag:</b> {detail.contact.tag || '—'}</div>
                  <div><b style={{ opacity:0.6 }}>Status:</b> {detail.contact.status || '—'}</div>
                  <div><b style={{ opacity:0.6 }}>Added:</b> {(detail.contact.created_at || '').slice(0,10) || '—'}</div>
                  <div><b style={{ opacity:0.6 }}>Archived:</b> {detail.contact.archived ? 'yes' : 'no'}</div>
                </div>
              </div>

              {/* Lead linkage */}
              {detail.lead ? (
                <div style={{ marginTop:14, padding:14, background:'rgba(52,209,176,0.06)', borderRadius:12, border:`1px solid ${teal}44` }}>
                  <b style={{ color:teal }}>Linked lead:</b> <span style={{ color:icy }}>#{detail.lead.lead_id} · stage: {detail.lead.stage}</span>
                </div>
              ) : detail.tombstone ? (
                <div style={{ marginTop:14, padding:14, background:'rgba(235,37,141,0.06)', borderRadius:12, border:'1px solid #EB258D44' }}>
                  <b style={{ color:magenta }}>Deleted lead (tombstoned):</b> <span style={{ color:'#FF7DB3' }}>{detail.tombstone.deleted_at?.slice(0,19)} — importer will not re-add</span>
                </div>
              ) : null}

              {/* Quotes */}
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:11, color:'#B5D5DD', letterSpacing:'.14em', textTransform:'uppercase', fontWeight:700, marginBottom:6 }}>Linked quotes ({detail.quotes.length})</div>
                {detail.quotes.length === 0 && <p style={{ color:'rgba(220,240,245,0.55)', fontSize:13, margin:0 }}>No quotes yet.</p>}
                {detail.quotes.map(q => (
                  <div key={q.quote_id} data-testid={`contact-detail-quote-${q.quote_id}`} style={{ padding:'8px 12px', margin:'4px 0', borderRadius:8, background:'rgba(255,255,255,0.03)', border:`1px solid ${line}`, fontSize:13, color:icy, display:'flex', gap:12 }}>
                    <span style={{ fontFamily:'monospace' }}>{q.quote_number}</span>
                    <span style={{ opacity:0.7 }}>{q.business_name}</span>
                    <span style={{ marginLeft:'auto', color:teal, fontWeight:700 }}>${(q.totals?.total_inc || 0).toLocaleString()}</span>
                    <span style={{ padding:'2px 8px', borderRadius:999, background:'rgba(220,240,245,0.06)', fontSize:11 }}>{q.status}</span>
                  </div>
                ))}
              </div>

              {/* Jobs */}
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:11, color:'#B5D5DD', letterSpacing:'.14em', textTransform:'uppercase', fontWeight:700, marginBottom:6 }}>Linked jobs ({detail.jobs.length})</div>
                {detail.jobs.length === 0 && <p style={{ color:'rgba(220,240,245,0.55)', fontSize:13, margin:0 }}>No jobs yet.</p>}
                {detail.jobs.map(j => (
                  <div key={j.job_id} style={{ padding:'8px 12px', margin:'4px 0', borderRadius:8, background:'rgba(255,255,255,0.03)', border:`1px solid ${line}`, fontSize:13, color:icy }}>
                    <span style={{ fontFamily:'monospace' }}>{j.job_id.slice(0,8)}</span>
                    <span style={{ marginLeft:8, opacity:0.7 }}>stage: {j.stage}</span>
                    <span style={{ marginLeft:8, color:teal, fontWeight:700 }}>${(j.value || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>

              {/* Sends + replies summary */}
              <div style={{ marginTop:14, padding:12, background:'rgba(255,255,255,0.03)', borderRadius:12, border:`1px solid ${line}`, display:'flex', gap:24, fontSize:13, color:icy }}>
                <div><b style={{ opacity:0.6 }}>Sends:</b> {detail.sends_count}</div>
                <div><b style={{ opacity:0.6 }}>Replies detected:</b> {detail.reply_count}</div>
                <div><b style={{ opacity:0.6 }}>Reply matches:</b> {detail.replies.length}</div>
              </div>

              {/* Actions */}
              <div style={{ marginTop:20, display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button data-testid="contact-detail-archive"
                  onClick={() => archiveContact(detailFor, !detail.contact.archived)}
                  style={{ ...ghost, padding:'8px 16px', fontSize:12, fontWeight:700 }}>
                  {detail.contact.archived ? '↺ Restore' : '🗄 Archive'}
                </button>
                <button data-testid="contact-detail-delete"
                  onClick={() => { deleteContacts([detailFor]); closeDetail(); }}
                  style={{ ...btn('transparent', '#FF7DB3'), padding:'8px 16px', fontSize:12, fontWeight:700, border:`1px solid ${magenta}55` }}>
                  Delete contact
                </button>
              </div>
            </>)}
          </div>
        </div>
      )}


      {/* CONFIRM SEND MODAL — the gate */}
      {confirmOpen && preflight && activeCampaign && (
        <div data-testid="outreach-confirm-modal" style={{
          position:'fixed', inset:0, background:'rgba(16,8,56,0.75)', backdropFilter:'blur(4px)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:100, padding:24,
        }}>
          <div style={{ background:surface, border:`1px solid ${line}`, borderRadius:16, maxWidth:560, width:'100%', padding:32 }}>
            <div style={{ fontSize:11, letterSpacing:'.22em', textTransform:'uppercase', color:magenta, fontWeight:700 }}>
              🔴 PRE-FLIGHT — you are about to email real people
            </div>
            <h2 style={{ margin:'14px 0 18px', fontFamily:'Poppins,Inter,sans-serif', fontSize:22, color:icy }}>
              {activeCampaign.name}
            </h2>
            <div style={{ display:'flex', gap:10, marginBottom:18 }}>
              <Stat label="Will receive" value={preflight.eligible_recipients} tint={teal} />
              <Stat label="Suppressed" value={preflight.suppressed} tint={magenta} />
              <Stat label="Throttle" value={`${preflight.throttle_per_hour}/hr`} />
              <Stat label="Est. duration" value={`${preflight.estimated_hours}h`} />
            </div>
            {!preflight.resend_verified && (
              <div style={{ background:'rgba(235,37,141,0.08)', border:'1px solid rgba(235,37,141,0.30)', borderRadius:8, padding:12, marginBottom:14, fontSize:13, color:'#FF7DB3' }}>
                ⚠ Resend reports the domain as NOT verified. Send anyway only if you&apos;ve just rotated DNS — otherwise deliverability will suffer.
              </div>
            )}
            <p style={{ fontSize:14, color:'#B5D5DD', marginBottom:24, lineHeight:1.55 }}>
              <strong>{preflight.eligible_recipients}</strong> real contacts will receive a personalised, branded email at a rate of <strong>{preflight.throttle_per_hour}/hr</strong>.
              Unsubscribed and bounced contacts are excluded automatically.
              You can pause at any time.
            </p>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button data-testid="confirm-cancel" onClick={() => setConfirmOpen(false)} style={ghost}>Cancel</button>
              <button data-testid="confirm-start" onClick={startCampaign} style={btn(magenta)} disabled={busy}>
                🚀 Yes, start sending to {preflight.eligible_recipients} {preflight.eligible_recipients === 1 ? 'person' : 'people'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QUICK-SEND PREVIEW MODAL — single-contact, click-is-the-gate. */}
      {quickTarget && (
        <div data-testid="quick-send-modal" style={{
          position:'fixed', inset:0, background:'rgba(16,8,56,0.80)', backdropFilter:'blur(4px)',
          display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:110, padding:24, overflowY:'auto',
        }}>
          <div style={{ background:surface, border:`1px solid ${line}`, borderRadius:16, maxWidth:720, width:'100%', padding:28, marginTop:24 }}>
            <div style={{ fontSize:11, letterSpacing:'.22em', textTransform:'uppercase', color:teal, fontWeight:700 }}>
              QUICK SEND · single contact · click-is-the-gate
            </div>
            <h2 style={{ margin:'14px 0 8px', fontFamily:'Poppins,Inter,sans-serif', fontSize:22, color:icy }}>
              Send &ldquo;{quickTarget.preview.subject}&rdquo; to {quickTarget.contact.first_name}
            </h2>
            <div style={{ background:'rgba(220,240,245,0.06)', border:'1px solid #2A2070', borderRadius:8, padding:'10px 12px', marginBottom:14, fontSize:13, color:'#B5D5DD' }}>
              <div><strong>To:</strong> {[quickTarget.contact.first_name, quickTarget.contact.last_name].filter(Boolean).join(' ')} · <span style={{ fontFamily:'monospace' }}>{quickTarget.contact.email}</span></div>
              <div><strong>Business:</strong> {quickTarget.contact.business_name || '—'}</div>
              {quickTarget.contact.phone && <div><strong>Phone:</strong> {quickTarget.contact.phone}</div>}
              <div style={{ marginTop:6, color:'rgba(220,240,245,0.55)' }}>
                Campaign: <strong>{activeCampaign?.name}</strong>
              </div>
              <div style={{ marginTop:6, fontSize:12, color:'#FF7DB3' }}>
                ⚠ Test mode active — email will be delivered to <strong>michael@kindraai.dev</strong> (not the real contact).
              </div>
            </div>
            <iframe
              data-testid="quick-send-preview"
              title="quick-send-preview"
              srcDoc={quickTarget.preview.html}
              style={{ width:'100%', height:520, border:'1px solid #2A2070', borderRadius:8, background:'#fff' }}
            />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
              <button data-testid="quick-send-cancel" onClick={() => setQuickTarget(null)} style={ghost}>Cancel</button>
              <button data-testid="quick-send-confirm" onClick={confirmQuickSend} style={btn(magenta)} disabled={busy}>
                {busy ? 'Sending…' : `✉ Send to ${quickTarget.contact.first_name}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div data-testid="outreach-toast" style={{
          position:'fixed', bottom:24, right:24, background:surface, border:`1px solid ${line}`, color:icy,
          padding:'14px 20px', borderRadius:12, fontSize:14, boxShadow:'0 12px 32px rgba(0,0,0,0.25)',
          zIndex:200, maxWidth:380,
        }}>{toast}</div>
      )}
    </Chrome>
  );
}
